import { useEffect, useMemo, useState } from 'react';
import {
  Form,
  Select,
  Input,
  InputNumber,
  DatePicker,
  Radio,
  Button,
  Table,
  Popconfirm,
  Empty,
  Upload,
  Collapse,
  Tag,
  Space,
  message,
} from 'antd';
import dayjs from 'dayjs';
import {
  DownloadOutlined,
  EyeOutlined,
  DeleteOutlined,
  PlusOutlined,
  FileWordOutlined,
  FileTextOutlined,
  EditOutlined,
  InboxOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  listDocumentTemplates,
  generateCompanyDocument,
  listCompanyDocuments,
  archiveDocumentTemplate,
} from '@/services/documentTemplates.service';
import { cn } from '@/libs/utils';
import { API } from '@/services/api.service';
import TemplateBuilderModal from '@/app/timesheet/_components/form/TemplateBuilderModal';
import { useAuthStore } from '@/store/useAuthStore';
import { ROLES } from '@/constants';

const { TextArea } = Input;

const BUSINESS_AUDIENCES = ['internal', 'external'];

/**
 * Company-level document generation (internal/external business documentation).
 * Not tied to any employee: pick a business template, fill its ad-hoc fields,
 * generate a stored CUSTOM document for the company, then download/preview it
 * from the list below. Mirrors the per-employee generate flow but company-scoped.
 */
export default function CompanyCustomDocsForm({
  companyId,
  // Profile: no card of its own and no title (the panel shows them).
  bare = false,
}) {
  const { t } = useTranslation(['forms', 'menus', 'tables', 'messages']);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const [templateId, setTemplateId] = useState(null);
  const [format, setFormat] = useState('pdf');
  // Only the two highest HLB roles may edit/archive global (cross-company) templates.
  const userRoles = useAuthStore((state) => state.user?.roles) || [];
  const canManageGlobal =
    userRoles.includes(ROLES.techAdmin) ||
    userRoles.includes(ROLES.superAdmin);
  // Template authoring (builder / configured DOCX upload) from this screen.
  const [builderOpen, setBuilderOpen] = useState(false);
  const [builderMode, setBuilderMode] = useState('builder');
  const [uploadFile, setUploadFile] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);

  const openNewBuilder = () => {
    setBuilderMode('builder');
    setUploadFile(null);
    setEditingTemplate(null);
    setBuilderOpen(true);
  };
  const openUploadBuilder = (file) => {
    setBuilderMode('upload');
    setUploadFile(file);
    setEditingTemplate(null);
    setBuilderOpen(true);
  };
  // Edit an existing business template. The modal opens in the editor for
  // builder templates and in config-edit mode for uploaded DOCX ones (it detects
  // `source` from the template).
  const openEditBuilder = (tpl) => {
    setBuilderMode('builder');
    setUploadFile(null);
    setEditingTemplate(tpl);
    setBuilderOpen(true);
  };

  const { data: allTemplates = [] } = useQuery({
    queryKey: ['documentTemplates', companyId, 'business'],
    queryFn: () => listDocumentTemplates(companyId),
    enabled: Boolean(companyId),
  });

  // Only internal/external templates are company (non-employee) documentation.
  const templates = useMemo(
    () => allTemplates.filter((tpl) => BUSINESS_AUDIENCES.includes(tpl.audience)),
    [allTemplates]
  );

  // Server-side pagination + search + date filter for the generated-documents
  // table (backend does skip/limit/count so the payload stays bounded).
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Debounce the search box; reset to the first page on any query change.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const docsParams = useMemo(
    () => ({
      search: search || undefined,
      dateFrom: dateRange?.[0]
        ? dateRange[0].startOf('day').toISOString()
        : undefined,
      dateTo: dateRange?.[1] ? dateRange[1].endOf('day').toISOString() : undefined,
      offset: (page - 1) * pageSize,
      limit: pageSize,
    }),
    [search, dateRange, page, pageSize]
  );

  const { data: docsData, status: docsStatus } = useQuery({
    queryKey: ['companyDocuments', companyId, docsParams],
    queryFn: () => listCompanyDocuments(companyId, docsParams),
    enabled: Boolean(companyId),
    placeholderData: keepPreviousData,
  });
  const documents = docsData?.items || [];
  const documentsTotal = docsData?.count || 0;

  const selected = useMemo(
    () => templates.find((tpl) => tpl._id === templateId) || null,
    [templates, templateId]
  );

  const generateMutation = useMutation({
    mutationFn: async () => {
      const values = selected?.customFields?.length
        ? await form.validateFields()
        : {};
      const customFieldValues = {};
      for (const field of selected?.customFields || []) {
        const raw = values[field.key];
        customFieldValues[field.key] =
          field.type === 'date' && raw
            ? dayjs(raw).format('DD.MM.YYYY')
            : raw != null
              ? String(raw)
              : '';
      }
      const { documentId } = await generateCompanyDocument({
        company: companyId,
        documentTemplateId: templateId,
        customFieldValues,
        title: selected?.name || '',
      });
      await API.documents.downloadDocument({
        documentId,
        type: format === 'doc' ? 'doc' : 'pdf',
        returnDoc: false,
      });
    },
    onSuccess: () => {
      message.success(t('forms:documentGenerated', 'Dokument generisan.'));
      queryClient.invalidateQueries({ queryKey: ['companyDocuments', companyId] });
    },
    onError: (error) => {
      if (error?.errorFields) return;
      message.error(
        t('forms:documentGenerateFailed', 'Generisanje nije uspelo.')
      );
    },
  });

  const archiveTemplateMutation = useMutation({
    mutationFn: (id) => archiveDocumentTemplate(id),
    onSuccess: (_data, id) => {
      message.success(t('forms:templateArchived', 'Šablon arhiviran.'));
      queryClient.invalidateQueries({
        queryKey: ['documentTemplates', companyId, 'business'],
      });
      // Drop the selection if the generate dropdown was pointing at it.
      if (templateId === id) setTemplateId(null);
    },
    onError: (error) =>
      message.error(
        error?.response?.data?.message ||
          t('forms:templateArchiveFailed', 'Arhiviranje nije uspelo.')
      ),
  });

  const deleteMutation = useMutation({
    mutationFn: (documentId) => API.documents.deleteDocumentById(documentId),
    onSuccess: () => {
      message.success(t('forms:documentDeleted', 'Dokument obrisan.'));
      queryClient.invalidateQueries({ queryKey: ['companyDocuments', companyId] });
    },
    onError: () =>
      message.error(t('forms:documentDeleteFailed', 'Brisanje nije uspelo.')),
  });

  const viewMutation = useMutation({
    mutationFn: (documentId) => API.documents.viewDocument(documentId),
    onError: (error) =>
      message.error(error?.response?.data?.message || t('messages:error')),
  });

  const downloadMutation = useMutation({
    mutationFn: (documentId) =>
      API.documents.downloadDocument({
        documentId,
        type: 'pdf',
        returnDoc: false,
      }),
    onError: (error) =>
      message.error(error?.response?.data?.message || t('messages:error')),
  });

  const renderFieldInput = (field) => {
    switch (field.type) {
      case 'number':
        return <InputNumber style={{ width: '100%' }} />;
      case 'date':
        return <DatePicker style={{ width: '100%' }} format="DD.MM.YYYY" />;
      case 'textarea':
        return <TextArea rows={3} />;
      case 'select':
        return (
          <Select
            options={(field.options || []).map((o) => ({ value: o, label: o }))}
          />
        );
      default:
        return <Input />;
    }
  };

  const columns = [
    {
      title: t('tables:name', 'Naziv'),
      dataIndex: 'title',
      render: (title) => title || '~',
    },
    {
      title: t('tables:createdAt', 'Kreirano'),
      dataIndex: 'createdAt',
      render: (createdAt) =>
        createdAt ? dayjs(createdAt).format('DD.MM.YYYY, HH:mm') : '~',
    },
    {
      title: t('tables:actions', 'Akcije'),
      key: 'actions',
      align: 'right',
      render: (_, record) => (
        <div className="flex justify-end gap-2">
          <Button
            size="small"
            icon={<EyeOutlined />}
            loading={viewMutation.isPending}
            onClick={() => viewMutation.mutate(record._id)}
          >
            {t('menus:viewPdf', 'Pregled (PDF)')}
          </Button>
          <Button
            size="small"
            icon={<DownloadOutlined />}
            loading={downloadMutation.isPending}
            onClick={() => downloadMutation.mutate(record._id)}
          />
          <Popconfirm
            title={t('forms:confirmDelete', 'Obrisati dokument?')}
            onConfirm={() => deleteMutation.mutate(record._id)}
            okText={t('forms:yes')}
            cancelText={t('forms:no')}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div className={cn(!bare && 'rounded-xl border p-6')}>
      {!bare && (
        <>
          <h2 className="mb-1 text-xl font-bold">
            {t('forms:companyDocuments', 'Kompanijski dokumenti')}
          </h2>
          <p className="mb-4 text-sm text-gray-500">
            {t(
              'forms:companyDocumentsHint',
              'Interna i eksterna poslovna dokumentacija — ne odnosi se na zaposlene.'
            )}
          </p>
        </>
      )}

      <div className="mb-4 flex flex-wrap gap-2">
        <Button
          type="primary"
          className="btn-primary"
          icon={<PlusOutlined />}
          onClick={openNewBuilder}
        >
          {t('forms:createTemplate', 'Napravi šablon')}
        </Button>
        <Upload
          accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          showUploadList={false}
          beforeUpload={(f) => {
            openUploadBuilder(f);
            return false;
          }}
        >
          <Button
            className="btn-default btn-default--hover"
            icon={<FileWordOutlined />}
          >
            {t('forms:uploadWordTemplate', 'Otpremi Word šablon')}
          </Button>
        </Upload>
      </div>

      {/* With no templates the empty state below already says so; an empty
          list here only repeated the same message. */}
      {templates.length > 0 && (
        <Collapse
          className="mb-4"
          size="small"
          items={[
            {
              key: 'templates',
              label: `${t('forms:businessTemplates', 'Šabloni (interni/eksterni)')} (${templates.length})`,
              children: (
                <div className="flex flex-col gap-2">
                  {templates.map((tpl) => (
                    <div
                      key={tpl._id}
                      className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-gray-200 px-3 py-2"
                    >
                      <Space wrap>
                        {tpl.source === 'upload' ? (
                          <FileWordOutlined />
                        ) : (
                          <FileTextOutlined />
                        )}
                        <span className="font-medium">{tpl.name}</span>
                        <Tag color={tpl.audience === 'external' ? 'gold' : 'cyan'}>
                          {t(`forms:templateAudience_${tpl.audience}`, tpl.audience)}
                        </Tag>
                        {tpl.source === 'upload' && <Tag color="blue">Word</Tag>}
                        {tpl.scope === 'global' && (
                          <Tag color="purple">
                            {t('forms:globalTemplateTag', 'Globalni')}
                          </Tag>
                        )}
                      </Space>
                      {/* Global (HLB-authored) templates are read-only unless the
                          user is techAdmin/superAdmin. */}
                      {(tpl.scope !== 'global' || canManageGlobal) && (
                        <Space>
                          <Button
                            size="small"
                            className="!rounded-[var(--buttonRadius)]"
                            icon={<EditOutlined />}
                            onClick={() => openEditBuilder(tpl)}
                          >
                            {tpl.source === 'upload'
                              ? t('forms:editConfig', 'Podešavanja')
                              : t('forms:edit', 'Izmeni')}
                          </Button>
                          <Popconfirm
                            title={t('forms:confirmArchiveTemplate', 'Arhivirati šablon?')}
                            okText={t('forms:yes')}
                            cancelText={t('forms:no')}
                            onConfirm={() => archiveTemplateMutation.mutate(tpl._id)}
                          >
                            <Button
                              size="small"
                              danger
                              className="!rounded-[var(--buttonRadius)]"
                              icon={<InboxOutlined />}
                            >
                              {t('forms:archive', 'Arhiviraj')}
                            </Button>
                          </Popconfirm>
                        </Space>
                      )}
                    </div>
                  ))}
                </div>
              ),
            },
          ]}
        />
      )}

      {templates.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t(
            'forms:noBusinessTemplates',
            'Nema šablona za internu/eksternu dokumentaciju. Napravi šablon sa namenom „Interna” ili „Eksterna”.'
          )}
        />
      ) : (
        <Form form={form} layout="vertical">
          <Form.Item label={t('forms:template', 'Šablon')} required>
            <Select
              value={templateId}
              onChange={setTemplateId}
              placeholder={t('forms:selectTemplate', 'Izaberi šablon')}
              options={templates.map((tpl) => ({
                value: tpl._id,
                label: tpl.name,
              }))}
            />
          </Form.Item>

          {(selected?.customFields || []).map((field) => (
            <Form.Item
              key={field.key}
              name={field.key}
              label={field.label || field.key}
              rules={field.required ? [{ required: true }] : []}
            >
              {renderFieldInput(field)}
            </Form.Item>
          ))}

          <Form.Item label={t('forms:format', 'Format')}>
            <Radio.Group
              value={format}
              onChange={(e) => setFormat(e.target.value)}
            >
              <Radio value="pdf">PDF</Radio>
              <Radio value="doc">Word</Radio>
            </Radio.Group>
          </Form.Item>

          <Button
            type="primary"
            className="btn-primary"
            disabled={!templateId}
            loading={generateMutation.isPending}
            onClick={() => generateMutation.mutate()}
          >
            {t('forms:generate')}
          </Button>
        </Form>
      )}

      <div className="mt-6">
        <h3 className="mb-2 text-base font-semibold">
          {t('forms:generatedDocuments', 'Generisani dokumenti')}
        </h3>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder={t('forms:searchByName', 'Pretraga po nazivu')}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            style={{ maxWidth: 260 }}
          />
          <DatePicker.RangePicker
            format="DD.MM.YYYY"
            value={dateRange}
            onChange={(range) => {
              setDateRange(range);
              setPage(1);
            }}
            placeholder={[
              t('forms:dateFrom', 'Datum od'),
              t('forms:dateTo', 'Datum do'),
            ]}
          />
        </div>
        <Table
          rowKey="_id"
          size="small"
          columns={columns}
          dataSource={documents}
          loading={docsStatus === 'pending'}
          pagination={{
            current: page,
            pageSize,
            total: documentsTotal,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
            showTotal: (tot) => t('forms:totalDocuments', '{{count}} dokumenata', {
              count: tot,
            }),
            onChange: (nextPage, nextSize) => {
              setPage(nextPage);
              setPageSize(nextSize);
            },
          }}
        />
      </div>

      <TemplateBuilderModal
        open={builderOpen}
        onClose={() => setBuilderOpen(false)}
        companyId={companyId}
        template={editingTemplate}
        mode={builderMode}
        file={uploadFile}
        defaultAudience="internal"
        onSaved={() =>
          queryClient.invalidateQueries({
            queryKey: ['documentTemplates', companyId, 'business'],
          })
        }
      />
    </div>
  );
}
